/**
 * Admin-Overwrite-Schutz: lastAdminUpdate-Stamp + Client-Write-Guard.
 * Eigenes Modul — vermeidet Circular Imports (adminStore ↔ firebaseSync).
 */
import { db } from './firebase';
import { doc, setDoc, getDocFromServer, serverTimestamp, updateDoc } from 'firebase/firestore';
import useAppStore from '../store/appStore';

const knownLastAdminUpdateMs: Record<string, number> = {};

export const toAdminUpdateMs = (value: any): number => {
    if (value === undefined || value === null || value === '') return 0;
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value?.toMillis === 'function') {
        try { return Number(value.toMillis()) || 0; } catch { /* ignore */ }
    }
    if (typeof value?.seconds === 'number') return Number(value.seconds) * 1000;
    const parsed = Date.parse(String(value));
    return Number.isFinite(parsed) ? parsed : 0;
};

export const rememberLastAdminUpdate = (uid: string, value: any): void => {
    if (!uid) return;
    const ms = toAdminUpdateMs(value);
    if (ms > 0) knownLastAdminUpdateMs[uid] = ms;
};

export const getKnownLastAdminUpdateMs = (uid: string): number =>
    (uid && knownLastAdminUpdateMs[uid]) || 0;

/** Admin-Write auf kalkulation_mandanten/{uid} (updateDoc) inkl. lastAdminUpdate. */
export const adminUpdateMandantRoot = async (
    uid: string,
    payload: Record<string, any>
): Promise<void> => {
    if (!uid) throw new Error('adminUpdateMandantRoot: uid fehlt');
    await updateDoc(doc(db, 'kalkulation_mandanten', uid), {
        ...payload,
        lastAdminUpdate: serverTimestamp(),
    });
};

/** Admin-Write auf kalkulation_mandanten/{uid} (setDoc merge) inkl. lastAdminUpdate. */
export const adminSetMandantRoot = async (
    uid: string,
    payload: Record<string, any>,
    merge: boolean = true
): Promise<void> => {
    if (!uid) throw new Error('adminSetMandantRoot: uid fehlt');
    await setDoc(
        doc(db, 'kalkulation_mandanten', uid),
        {
            ...payload,
            lastAdminUpdate: serverTimestamp(),
        },
        { merge }
    );
};

/** Nur Stamp setzen. */
export const touchLastAdminUpdate = async (uid: string): Promise<void> => {
    if (!uid) return;
    await updateDoc(doc(db, 'kalkulation_mandanten', uid), {
        lastAdminUpdate: serverTimestamp(),
    });
};

/**
 * true = Client-Write darf weiterlaufen.
 * false = Server hat neueren Admin-Stamp → Write abgebrochen, Re-Fetch angestoßen.
 * God-Mode/Impersonation: immer true (skip).
 */
export const guardClientWriteAgainstAdminUpdate = async (
    targetUid: string,
    isGodMode: boolean
): Promise<boolean> => {
    if (!targetUid || isGodMode) return true;
    try {
        const snap = await getDocFromServer(doc(db, 'kalkulation_mandanten', targetUid));
        if (!snap.exists()) return true;
        const serverMs = toAdminUpdateMs(snap.data()?.lastAdminUpdate);
        if (!serverMs) return true;

        const knownMs = getKnownLastAdminUpdateMs(targetUid);
        if (serverMs <= knownMs) return true;

        console.warn(
            '[AdminOverwriteGuard] lastAdminUpdate auf Server neuer — Client-Write abgebrochen, Re-Fetch',
            { targetUid, serverMs, knownMs }
        );

        try {
            const init = useAppStore.getState().initializeStore as
                | ((uid?: string) => Promise<void> | void)
                | undefined;
            if (typeof init === 'function') {
                await init(targetUid);
            }
        } catch (reFetchErr) {
            console.error('[AdminOverwriteGuard] Re-Fetch fehlgeschlagen:', reFetchErr);
            rememberLastAdminUpdate(targetUid, serverMs);
        }
        return false;
    } catch (err) {
        console.warn('[AdminOverwriteGuard] Vergleich nicht möglich — Write erlaubt:', err);
        return true;
    }
};
